import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import type { CargoType, TransportMode, WeightUnit } from '@/lib/freight'

export interface FreightLead {
  _id: string
  fullName: string
  businessEmail: string
  phoneNumber: string
  cargoReadyDate: string
  transportMode: TransportMode
  estimatedRate: number
  origin?: string
  destination?: string
  weight?: number
  weightUnit?: WeightUnit
  cargoType?: CargoType
  creator?: string
  createdAt: string
  updatedAt: string
}

export type SortKey = 'createdAt' | 'estimatedRate' | 'fullName'
export type SortDir = 'asc' | 'desc'

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export function useFreightLeads() {
  // Data
  const [leads, setLeads] = useState<FreightLead[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Aggregate stats scope to the active filters (independent of pagination/sort)
  const [statsLeads, setStatsLeads] = useState<FreightLead[]>([])
  const [statsLoading, setStatsLoading] = useState(false)

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>('createdAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Advanced filters
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [modeFilter, setModeFilter] = useState<TransportMode | 'ALL'>('ALL')
  const [cargoTypeFilter, setCargoTypeFilter] = useState<CargoType | 'ALL'>('ALL')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [minRate, setMinRate] = useState('')
  const [maxRate, setMaxRate] = useState('')

  // Debounce free-text search so we don't hit the database on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(t)
  }, [search])

  const buildFilter = (): Record<string, unknown> => {
    const filter: Record<string, unknown> = {}

    if (modeFilter !== 'ALL') filter.transportMode = modeFilter
    if (cargoTypeFilter !== 'ALL') filter.cargoType = cargoTypeFilter

    if (debouncedSearch) {
      const regex = { $regex: debouncedSearch, $options: 'i' }
      filter.$or = [
        { fullName: regex },
        { businessEmail: regex },
        { phoneNumber: regex },
        { origin: regex },
        { destination: regex },
      ]
    }

    if (dateFrom || dateTo) {
      const range: Record<string, string> = {}
      if (dateFrom) range.$gte = `${dateFrom}T00:00:00.000Z`
      if (dateTo) range.$lte = `${dateTo}T23:59:59.999Z`
      filter.cargoReadyDate = range
    }

    const min = minRate !== '' ? Number(minRate) : undefined
    const max = maxRate !== '' ? Number(maxRate) : undefined
    if ((min !== undefined && !Number.isNaN(min)) || (max !== undefined && !Number.isNaN(max))) {
      const range: Record<string, number> = {}
      if (min !== undefined && !Number.isNaN(min)) range.$gte = min
      if (max !== undefined && !Number.isNaN(max)) range.$lte = max
      filter.estimatedRate = range
    }

    return filter
  }

  const fetchLeads = async () => {
    setLoading(true)
    setError(null)
    try {
      const filter = buildFilter()
      const { list, total: count } = await lumi.entities.freight_leads.list({
        filter,
        sort: { [sortKey]: sortDir === 'asc' ? 1 : -1 },
        limit: pageSize,
        skip: (page - 1) * pageSize,
      })
      setLeads(list as FreightLead[])
      setTotal(count)
    } catch (err) {
      console.error('Failed to fetch freight leads:', err)
      setError('Unable to load leads. Administrator access is required to view this data.')
    } finally {
      setLoading(false)
    }
  }

  // Fetches every record matching the active filters (ignores pagination) so stat
  // cards reflect the full filtered dataset rather than just the current page
  const fetchStats = async () => {
    setStatsLoading(true)
    try {
      const filter = buildFilter()
      const { list } = await lumi.entities.freight_leads.list({
        filter,
        sort: { createdAt: -1 },
        limit: 5000,
      })
      setStatsLeads(list as FreightLead[])
    } catch (err) {
      console.error('Failed to fetch lead stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }

  // Re-fetch whenever page, page size, sort, or any filter (primitive values) changes
  useEffect(() => {
    fetchLeads()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortKey, sortDir, debouncedSearch, modeFilter, cargoTypeFilter, dateFrom, dateTo, minRate, maxRate])

  // Stats only depend on filters, not pagination or sort
  useEffect(() => {
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, modeFilter, cargoTypeFilter, dateFrom, dateTo, minRate, maxRate])

  // Filter/sort setters all reset pagination back to page 1
  const updateSearch = (v: string) => { setSearch(v); setPage(1) }
  const updateModeFilter = (v: TransportMode | 'ALL') => { setModeFilter(v); setPage(1) }
  const updateCargoTypeFilter = (v: CargoType | 'ALL') => { setCargoTypeFilter(v); setPage(1) }
  const updateDateFrom = (v: string) => { setDateFrom(v); setPage(1) }
  const updateDateTo = (v: string) => { setDateTo(v); setPage(1) }
  const updateMinRate = (v: string) => { setMinRate(v); setPage(1) }
  const updateMaxRate = (v: string) => { setMaxRate(v); setPage(1) }
  const updatePageSize = (v: number) => { setPageSize(v); setPage(1) }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
    setPage(1)
  }

  const resetFilters = () => {
    setSearch('')
    setModeFilter('ALL')
    setCargoTypeFilter('ALL')
    setDateFrom('')
    setDateTo('')
    setMinRate('')
    setMaxRate('')
    setPage(1)
  }

  const hasActiveFilters = Boolean(
    debouncedSearch || modeFilter !== 'ALL' || cargoTypeFilter !== 'ALL' || dateFrom || dateTo || minRate || maxRate
  )

  const deleteLead = async (id: string) => {
    try {
      await lumi.entities.freight_leads.delete(id)
      toast.success('Lead removed')
      if (leads.length === 1 && page > 1) {
        // Current page would become empty; step back a page (triggers refetch via effect)
        setPage((p) => p - 1)
      } else {
        await fetchLeads()
      }
      await fetchStats()
    } catch (err) {
      console.error('Failed to delete lead:', err)
      toast.error('Failed to delete lead. Please try again.')
      throw err
    }
  }

  // Fetch every record matching current filters/sort (ignores pagination) for CSV export
  const fetchAllMatching = async (): Promise<FreightLead[]> => {
    try {
      const filter = buildFilter()
      const { list } = await lumi.entities.freight_leads.list({
        filter,
        sort: { [sortKey]: sortDir === 'asc' ? 1 : -1 },
        limit: total > 0 ? total : 1000,
      })
      return list as FreightLead[]
    } catch (err) {
      console.error('Failed to fetch leads for export:', err)
      toast.error('Failed to prepare export. Please try again.')
      return []
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return {
    leads, total, totalPages, loading, error, fetchLeads, deleteLead, fetchAllMatching,
    statsLeads, statsLoading,
    page, setPage, pageSize, setPageSize: updatePageSize,
    sortKey, sortDir, toggleSort,
    search, setSearch: updateSearch,
    modeFilter, setModeFilter: updateModeFilter,
    cargoTypeFilter, setCargoTypeFilter: updateCargoTypeFilter,
    dateFrom, setDateFrom: updateDateFrom,
    dateTo, setDateTo: updateDateTo,
    minRate, setMinRate: updateMinRate,
    maxRate, setMaxRate: updateMaxRate,
    resetFilters, hasActiveFilters,
  }
}
